import {
  FormAutocompleteMultiInput,
  FormToggleSwitch,
  GlobalButton,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import type { Option } from '@/components/inputs/AutocompleteInput';
import type { Control } from 'react-hook-form';

export interface CommerceAccessCardSearch {
  inputValue: string;
  onInputChange: (value: string) => void;
  labelsById: Record<string, string>;
  isSearching: boolean;
  isLoadingBranches: boolean;
}

export interface CommerceAccessCardBulkBranches {
  total: number;
  selectedCount: number;
  areAllSelected: boolean;
  isReady: boolean;
  onSelectAll: () => void;
  onClear: () => void;
}

interface CommerceAccessCardProps {
  control: Control<any>;
  isLoadingOptions: boolean;
  commercesOptions: Option[];
  commercesIncludeAll: boolean;
  isCommerceRequired: boolean;
  hasIncludedCommerces: boolean;
  branchesOptions: Option[];
  branchesIncludeAll: boolean;
  showExcludeCommercesToggle?: boolean;
  showExcludeBranchesToggle?: boolean;
  bulkBranches?: CommerceAccessCardBulkBranches;
  search?: CommerceAccessCardSearch;
}

const CommerceAccessCard = ({
  control,
  isLoadingOptions,
  commercesOptions,
  commercesIncludeAll,
  isCommerceRequired,
  hasIncludedCommerces,
  branchesOptions,
  branchesIncludeAll,
  showExcludeCommercesToggle = true,
  showExcludeBranchesToggle = true,
  bulkBranches,
  search,
}: CommerceAccessCardProps) => (
  <div className='min-w-0 flex-1 rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
    <h3 className='mb-4 text-lg font-semibold text-gray-800'>Comercio</h3>
    <div className='min-w-0 overflow-hidden pt-3'>
      <FormAutocompleteMultiInput
        name='commerces.selected'
        placeholder={
          isLoadingOptions && !search ? 'Cargando...' : 'Seleccionar comercios'
        }
        control={control}
        options={commercesOptions}
        disabled={commercesIncludeAll}
        required={isCommerceRequired}
        {...(search && {
          inputValue: search.inputValue,
          onInputChange: search.onInputChange,
          labelsById: search.labelsById,
          loading: search.isSearching,
          keepOptionsOrder: true,
          compactSelection: true,
          keepInputOnSelect: true,
        })}
      />
      <p className='mt-1 text-xs text-gray-500'>
        {search
          ? 'Escribí el nombre para buscar entre todos los comercios activos.'
          : 'Selecciona los comercios que quieras incluir.'}
      </p>
    </div>
    {(showExcludeCommercesToggle || commercesIncludeAll) && (
      <div className='mt-4 flex items-center'>
        <FormToggleSwitch name='commerces.includeAll' control={control} />
        <span className='ml-2 text-sm text-gray-700'>
          ¿Desea excluir comercios?
        </span>
      </div>
    )}
    {commercesIncludeAll && (
      <div className='min-w-0 overflow-hidden pt-3'>
        <FormAutocompleteMultiInput
          name='commerces.excluded'
          placeholder='Excluir comercios'
          control={control}
          options={commercesOptions}
          disabled={!commercesIncludeAll}
          {...(search && {
            inputValue: search.inputValue,
            onInputChange: search.onInputChange,
            labelsById: search.labelsById,
            loading: search.isSearching,
          })}
        />
        <p className='mt-1 text-xs text-gray-500'>
          Selecciona los comercios que quieras excluir (todos los demás
          estarán incluidos).
        </p>
      </div>
    )}
    {hasIncludedCommerces && (
      <div className='mt-6 border-t border-gray-200 pt-6'>
        <h4 className='text-md mb-4 font-semibold text-gray-700'>Sucursal</h4>
        <div className='min-w-0 overflow-hidden pt-2'>
          <FormAutocompleteMultiInput
            name='branches.selected'
            placeholder='Seleccionar sucursales'
            control={control}
            options={branchesOptions}
            disabled={branchesIncludeAll}
            loading={search?.isLoadingBranches ?? false}
            compactSelection
          />
          <div className='mt-1 flex flex-wrap items-center justify-between gap-2'>
            <p className='text-xs text-gray-500'>
              Selecciona las sucursales que quieras incluir.
            </p>
            {bulkBranches && (
              <div className='flex items-center gap-1'>
                <GlobalButton
                  variant={GlobalButtonVariant.textButton}
                  action={bulkBranches.onSelectAll}
                  disabled={!bulkBranches.isReady || bulkBranches.areAllSelected}
                  size='small'
                >
                  {bulkBranches.isReady
                    ? `Seleccionar todas (${bulkBranches.total})`
                    : 'Seleccionar todas'}
                </GlobalButton>
                {bulkBranches.selectedCount > 0 && (
                  <GlobalButton
                    variant={GlobalButtonVariant.textButton}
                    action={bulkBranches.onClear}
                    disabled={branchesIncludeAll}
                    size='small'
                  >
                    Quitar todas
                  </GlobalButton>
                )}
              </div>
            )}
          </div>
        </div>
        {(showExcludeBranchesToggle || branchesIncludeAll) && (
          <div className='mt-4 flex items-center'>
            <FormToggleSwitch name='branches.includeAll' control={control} />
            <span className='ml-2 text-sm text-gray-700'>
              ¿Desea excluir sucursales?
            </span>
          </div>
        )}
        {branchesIncludeAll && (
          <div className='min-w-0 overflow-hidden pt-3'>
            <FormAutocompleteMultiInput
              name='branches.excluded'
              placeholder='Excluir sucursales'
              control={control}
              options={branchesOptions}
              disabled={!branchesIncludeAll}
              loading={search?.isLoadingBranches ?? false}
            />
            <p className='mt-1 text-xs text-gray-500'>
              Selecciona las sucursales que quieras excluir (todas las demás
              estarán incluidas).
            </p>
          </div>
        )}
      </div>
    )}
  </div>
);

export default CommerceAccessCard;
